import React from 'react';
import { CzechSuklRsAtcGroupResponse } from 'com.cogvio.pricemonitor.api';
import { Checkbox } from '~/libs/ui/atoms/Checkbox';
import { TailwindTooltip } from '~/libs/ui/atoms/TailwindTooltip';

import { getAvailabilityTooltip } from '../utils/availabilityTooltip';
import { resolveAvailabilityMetric } from '../utils/resolveAvailabilityMetric';
import { AvailabilityShareBar } from './AvailabilityShareBar';
import { RowAvailabilityIcon } from './RowAvailabilityIcon';
import { TruncatedText } from './TruncatedText';

export interface RowProps {
	group: CzechSuklRsAtcGroupResponse;
	dddShareThreshold: number;
	selectable?: boolean;
	selected?: boolean;
	selectionDisabled?: boolean;
	/** Hides the checkbox control while keeping selection working via clicking the row. */
	hideCheckbox?: boolean;
	onToggleSelect?(group: CzechSuklRsAtcGroupResponse, selected: boolean): void;
}

function FieldLabel(title: string) {
	return <span className='block mb-1 text-labelsmall text-gray-500 leading-tight'>{title}</span>;
}

export function Row({
	group,
	dddShareThreshold,
	selectable,
	selected,
	selectionDisabled,
	hideCheckbox,
	onToggleSelect,
}: RowProps) {
	const rows = group.products.flatMap((product) => product.productVersions.map((version) => ({ product, version })));

	const [atcCode, ...atcRest] = group.atc.split(' ');
	const activeSubstance = atcRest.join(' ');

	const canClickToSelect = selectable && hideCheckbox && !(selectionDisabled && !selected);

	return (
		<div
			className={`flex flex-wrap xl:flex-nowrap w-full justify-start items-start font-display${
				selectable && hideCheckbox
					? ` -m-2 p-2 rounded-lg border transition-colors${canClickToSelect ? ' cursor-pointer' : ''}${
							selected ? ' border-cBlue-500 bg-cBlue-50' : ' border-transparent hover:border-gray-200'
					  }`
					: ''
			}`}
			onClick={canClickToSelect ? () => onToggleSelect?.(group, !selected) : undefined}
		>
			{selectable && !hideCheckbox && (
				<div className='w-full sm:w-auto xl:mb-0 mb-3 sm:pr-3 pt-0.5'>
					<Checkbox
						id={`availability-row-select-${group.id}`}
						checked={Boolean(selected)}
						disabled={selectionDisabled && !selected}
						onChange={(checked) => onToggleSelect?.(group, checked)}
					/>
				</div>
			)}
			<div className='w-1/2 sm:w-14 xl:mb-0 mb-3'>
				{FieldLabel('RS')}
				<h2 className='text-md font-semibold text-gray-900 leading-5 uppercase tabular-nums'>{group.rs ?? '–'}</h2>
			</div>
			<div className='w-1/2 sm:w-64 min-w-0 xl:mb-0 mb-3'>
				{FieldLabel('ATC')}
				<TailwindTooltip
					reference={
						<h2 className='flex min-w-0 text-base font-bold text-gray-900 leading-5 uppercase'>
							<span className='flex-shrink-0 text-cBlue-500 mr-2 tabular-nums'>{atcCode}</span>
							<span className='min-w-0 truncate'>{activeSubstance}</span>
						</h2>
					}
					displayClass='block min-w-0 max-w-full'
				>
					{group.atc}
				</TailwindTooltip>
			</div>
			<div className='w-full min-w-0 xl:w-auto xl:flex-shrink xl:flex-grow'>
				{rows.map((row, index) => {
					const showBrand = index === 0 || rows[index - 1].version.brand !== row.version.brand;
					const showSeparator = showBrand && index !== 0;
					const showLabel = index === 0;
					const metric = resolveAvailabilityMetric(row.product.availability);
					const dncDou = [row.version.dnc1, row.version.puhr].filter(Boolean).join(' / ') || '–';
					return (
						<div
							className={`flex flex-wrap xl:flex-nowrap w-full min-w-0 justify-start items-start gap-x-2 pb-3${
								showSeparator ? ' pt-3 border-t border-gray-300' : ''
							}`}
							key={row.version.suklCode ?? `${group.id}-${index}`}
						>
							<div className='w-1/2 sm:w-40 min-w-0 flex-shrink-0 mb-2 xl:mb-0'>
								{showLabel && FieldLabel('NÁZEV LP')}
								{showBrand ? (
									<TruncatedText
										text={row.version.brand ?? ''}
										className='text-base font-bold text-gray-900 leading-5 uppercase'
									/>
								) : null}
							</div>
							<div className='w-1/2 sm:min-w-0 sm:flex-1 sm:max-w-48 mb-2 xl:mb-0'>
								{showLabel && FieldLabel('DOPLNĚK NÁZVU')}
								<TruncatedText
									text={row.version.description ?? ''}
									className='text-md font-semibold text-gray-900 leading-5 uppercase tabular-nums'
								/>
							</div>
							<div className='w-1/2 sm:w-16 flex-shrink-0 mb-2 xl:mb-0'>
								{showLabel && FieldLabel('SÚKL kód')}
								<h2 className='text-label font-semibold text-gray-900 leading-5 uppercase tabular-nums'>
									{row.version.suklCode ?? '–'}
								</h2>
							</div>
							<div className='w-full sm:w-52 flex-shrink-0 mb-2 xl:mb-0'>
								{showLabel && FieldLabel('DOSTUPNOST')}
								<AvailabilityShareBar
									label={metric.label}
									value={metric.value}
									share={metric.share}
									shareThreshold={dddShareThreshold}
								/>
							</div>
							<div className='w-full sm:w-14 mb-2 sm:mb-0 sm:text-center'>
								{showLabel && FieldLabel('DNC/DoÚ')}
								<div className='flex sm:block justify-between sm:justify-center items-center w-full sm:pt-5 text-sm leading-6'>
									<span className='sm:hidden text-labelsmall text-gray-500 uppercase'>DNC/DoÚ</span>
									<span>{dncDou}</span>
								</div>
							</div>
							<div className='w-full sm:w-14 sm:text-center'>
								{showLabel && FieldLabel('-')}
								<div className='flex sm:block justify-between sm:justify-center items-center w-full sm:pt-6'>
									<span className='sm:hidden text-labelsmall text-gray-500 uppercase'>Stav</span>
									<TailwindTooltip
										reference={<RowAvailabilityIcon status={row.product.availability.status} />}
										displayClass='relative inline-block'
										position='left'
									>
										{getAvailabilityTooltip({
											status: row.product.availability.status,
											isOverThreshold: row.product.availability.decisionDetails.isOverThreshold,
											isFullyReimbursed: row.product.availability.decisionDetails.isFullyReimbursed,
											hasPriceAgreement: row.product.availability.decisionDetails.hasPriceAgreement,
											thresholdPercent: dddShareThreshold,
										})}
									</TailwindTooltip>
								</div>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
}
